import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX, FiMove } from "react-icons/fi";
import { TemporaryImage } from "../types";

type LogoTransformModalProps = {
    open: boolean;
    disabled: boolean;
    image: TemporaryImage | null;
    anchorRef: React.RefObject<HTMLElement | null>;
    onClose: () => void;
    onUpdate: (image_id: number, properties: Partial<TemporaryImage>) => void;
};

export const LogoTransformModal = ({
    open,
    disabled,
    image,
    anchorRef,
    onClose,
    onUpdate
}: LogoTransformModalProps) => {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isMobile, setIsMobile] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef<{ x: number; y: number } | null>(null);
    const [localValues, setLocalValues] = useState({
        xPosition: "0",
        yPosition: "0",
        scale: "1",
        rotation: "0"
    });
    const isEditingRef = useRef(false);
    const lastImageIdRef = useRef<number | null>(null);

    useEffect(() => {
        if (!open || !image) return;
        const imageId = image.id ?? null;
        const isNewImage = imageId !== lastImageIdRef.current;
        if (isNewImage) {
            lastImageIdRef.current = imageId;
            isEditingRef.current = false;
        }
        if (isEditingRef.current) return;
        setLocalValues({
            xPosition: String(Math.round(image.x_position ?? 0)),
            yPosition: String(Math.round(image.y_position ?? 0)),
            scale: String(image.scale.toFixed(2) ?? 1),
            rotation: String(Math.round(image.rotation ?? 0))
        });
    }, [open, image?.id, image?.x_position, image?.y_position, image?.scale, image?.rotation]);


    useEffect(() => {
        if (!open || !anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        setPosition({
            x: rect.left + 24,
            y: rect.top + 24
        });
    }, [open, anchorRef]);

    useEffect(() => {
        const media = window.matchMedia("(max-width: 767px)");
        const update = () => setIsMobile(media.matches);
        update();
        media.addEventListener("change", update);
        return () => media.removeEventListener("change", update);
    }, []);

    // --- Drag Logic ---
    const handlePointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        setIsDragging(true);
        dragStartRef.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        };
        // Capture pointer to ensure smooth dragging even if cursor leaves the handle
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isDragging || !dragStartRef.current) return;

        let newX = e.clientX - dragStartRef.current.x;
        let newY = e.clientY - dragStartRef.current.y;

        setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = (e: React.PointerEvent) => {
        setIsDragging(false);
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    };

    // --- Change Handlers ---

    const handleXPositionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!image || disabled) return;
        const nextValue = e.target.value;
        setLocalValues((prev) => ({ ...prev, xPosition: nextValue }));
        const newX = Number(nextValue);
        if (!Number.isFinite(newX)) return;
        onUpdate(image.id, { x_position: Math.round(newX) });
    };

    const handleYPositionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!image || disabled) return;
        const nextValue = e.target.value;
        setLocalValues((prev) => ({ ...prev, yPosition: nextValue }));
        const newY = Number(nextValue);
        if (!Number.isFinite(newY)) return;
        onUpdate(image.id, { y_position: Math.round(newY) });
    };

    const handleScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!image || disabled) return;
        const nextValue = e.target.value;
        setLocalValues((prev) => ({ ...prev, scale: nextValue }));
        const newScale = Number(nextValue);
        if (!Number.isFinite(newScale)) return;
        onUpdate(image.id, { scale: newScale });
    };

    const handleRotationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!image || disabled) return;
        const nextValue = e.target.value;
        setLocalValues((prev) => ({ ...prev, rotation: nextValue }));
        let newRotation = Number(nextValue);
        if (!Number.isFinite(newRotation)) return;
        newRotation = newRotation % 360;
        onUpdate(image.id, { rotation: newRotation });
    };

    if (!open || !image) return null;

    const modal = (
        <div
            className="fixed z-50 bg-white border border-gray-200 w-full md:w-64 overflow-hidden flex flex-col rounded-none md:rounded-lg"
            style={{
                left: isMobile ? 0 : position.x,
                top: isMobile ? 0 : position.y,
                right: isMobile ? 0 : "auto",
                touchAction: "none"
            }}
        >
            {/* Header / Drag Handle */}
            <div
                className={`flex items-center justify-between p-1 md:p-2 bg-gray-50 border-b border-gray-200 cursor-move ${isDragging ? 'cursor-grabbing' : ''}`}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            >
                <div className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide select-none">
                    <FiMove /> Transform
                </div>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-700 transition-colors p-1"
                >
                    <FiX size={16} />
                </button>
            </div>

            {/* Content */}
            <div className="p-2 md:p-3 grid gap-2 md:gap-3 text-sm">
                
                {/* Position */}
                <div className="flex flex-row flex-nowrap items-center gap-2 md:grid md:grid-cols-2 md:gap-2">
                    <div className="flex items-center gap-1 md:flex-col md:items-start md:gap-1">
                        <label className="text-xs text-gray-500 font-medium whitespace-nowrap">X Pos</label>
                        <input
                            type="number"
                            value={localValues.xPosition}
                            onChange={handleXPositionChange}
                            disabled={disabled}
                            onFocus={() => { isEditingRef.current = true; }}
                            onBlur={() => { isEditingRef.current = false; }}
                            className="w-[8ch] md:w-full px-2 py-1 border border-gray-300 rounded focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs"
                        />
                    </div>
                    <div className="flex items-center md:flex-col md:items-start">
                        <label className="text-xs text-gray-500 font-medium whitespace-nowrap">Y Pos</label>
                        <input
                            type="number"
                            value={localValues.yPosition}
                            onChange={handleYPositionChange}
                            disabled={disabled}
                            onFocus={() => { isEditingRef.current = true; }}
                            onBlur={() => { isEditingRef.current = false; }}
                            className="w-[8ch] md:w-full px-2 py-1 border border-gray-300 rounded focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500 text-xs"
                        />
                    </div>
                </div>

                {/* Scale */}
                <div className="flex flex-col">
                    <div className="flex justify-between">
                        <label className="text-xs text-gray-500 font-medium">Scale Factor</label>
                        <span className="text-xs text-gray-400">{localValues.scale || "1"}x</span>
                    </div>
                    <div className="flex items-center">
                        <input
                            type="range"
                            min="0.1"
                            max="5"
                            step="0.1"
                            value={Number.isFinite(Number(localValues.scale)) ? Number(localValues.scale) : image.scale || 1}
                            onChange={handleScaleChange}
                            disabled={disabled}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
                        />
                        <input
                            type="number"
                            step="0.1"
                            value={localValues.scale}
                            onChange={handleScaleChange}
                            disabled={disabled}
                            onFocus={() => { isEditingRef.current = true; }}
                            onBlur={() => { isEditingRef.current = false; }}
                            className="w-14 px-1 py-0.5 border border-gray-300 rounded text-center text-xs focus:border-orange-500 focus:outline-none"
                        />
                    </div>
                </div>

                {/* Rotation */}
                <div className="flex flex-col">
                    <div className="flex justify-between">
                        <label className="text-xs text-gray-500 font-medium">Rotation (°)</label>
                        <span className="text-xs text-gray-400">{localValues.rotation || "0"}°</span>
                    </div>
                     <div className="flex items-center">
                        <input
                            type="range"
                            min="-360"
                            max="360"
                            step="1"
                            value={Number.isFinite(Number(localValues.rotation)) ? Number(localValues.rotation) : image.rotation || 0}
                            onChange={handleRotationChange}
                            disabled={disabled}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-600"
                        />
                         <input
                            type="number"
                            value={localValues.rotation}
                            onChange={handleRotationChange}
                            disabled={disabled}
                            onFocus={() => { isEditingRef.current = true; }}
                            onBlur={() => { isEditingRef.current = false; }}
                            className="w-14 px-1 py-0.5 border border-gray-300 rounded text-center text-xs focus:border-orange-500 focus:outline-none"
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};
